import type {
  TeamPlayerFinderHiddenReason,
  TeamPlayerFinderOptInBlocker,
} from '../types'

/** One sentence to show: an i18n key and its interpolation values. */
export interface PlayerFinderExplanation {
  key: string
  params?: Record<string, string>
}

const HIDDEN_REASON_KEYS: Record<TeamPlayerFinderHiddenReason, string> = {
  no_listing: 'common:teamPlayerFinderHiddenNoListing',
  recruiting_off: 'common:teamPlayerFinderHiddenRecruitingOff',
  finder_disabled: 'common:teamPlayerFinderHiddenFinderDisabled',
  no_listed_slots: 'common:teamPlayerFinderHiddenNoListedSlots',
  roster_full: 'common:teamPlayerFinderHiddenRosterFull',
  registration_not_approved:
    'common:teamPlayerFinderHiddenRegistrationNotApproved',
  registration_cancelled: 'common:teamPlayerFinderHiddenRegistrationCancelled',
  registration_missing: 'common:teamPlayerFinderHiddenRegistrationMissing',
  registration_private: 'common:teamPlayerFinderHiddenRegistrationPrivate',
}

const OPT_IN_BLOCKER_KEYS: Record<TeamPlayerFinderOptInBlocker, string> = {
  cancelled: 'common:teamPlayerFinderBlockerCancelled',
  not_approved: 'common:teamPlayerFinderBlockerNotApproved',
  finder_disabled: 'common:teamPlayerFinderBlockerFinderDisabled',
  recruiting_elsewhere: 'common:teamPlayerFinderBlockerRecruitingElsewhere',
}

/** C7 without a resolvable division name still has to say what to do. */
const RECRUITING_ELSEWHERE_UNNAMED_KEY =
  'common:teamPlayerFinderBlockerRecruitingElsewhereUnnamed'

const lookup = (map: Record<string, string>, code: string): string | null =>
  Object.prototype.hasOwnProperty.call(map, code) ? map[code] : null

/**
 * The server's codes as sentences, in the server's order. The server owns the
 * vocabulary, so a code this build does not know is skipped — never rendered
 * raw — and a sentence is never repeated.
 */
const explain = (
  codes: readonly string[],
  explanationFor: (code: string) => PlayerFinderExplanation | null
): PlayerFinderExplanation[] =>
  codes.reduce<PlayerFinderExplanation[]>((explanations, code) => {
    const explanation = explanationFor(code)
    if (
      explanation &&
      !explanations.some((existing) => existing.key === explanation.key)
    ) {
      explanations.push(explanation)
    }

    return explanations
  }, [])

/** Why Player Finder search does not show the team (`hidden_reasons`). */
export const hiddenReasonExplanations = (
  codes: readonly string[]
): PlayerFinderExplanation[] =>
  explain(codes, (code) => {
    const key = lookup(HIDDEN_REASON_KEYS, code)

    return key ? { key } : null
  })

/**
 * Why recruiting cannot be turned on (`opt_in_blockers`). `recruiting_elsewhere`
 * names the division already recruiting when the server resolved it.
 */
export const optInBlockerExplanations = (
  codes: readonly string[],
  blockingDivision: string | null
): PlayerFinderExplanation[] =>
  explain(codes, (code) => {
    if (code === 'recruiting_elsewhere') {
      return blockingDivision
        ? {
            key: OPT_IN_BLOCKER_KEYS.recruiting_elsewhere,
            params: { division: blockingDivision },
          }
        : { key: RECRUITING_ELSEWHERE_UNNAMED_KEY }
    }
    const key = lookup(OPT_IN_BLOCKER_KEYS, code)

    return key ? { key } : null
  })
